import { useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import { useRef, useEffect, useState, useCallback } from 'react';

import { useIntersectionObserver, useLoadMoreBackoff } from '@common/hooks';
import { useAppStore } from '@common/stores/useAppStore';
import { Badge } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';
import { useEventEmit } from '@common/utils/events';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { ChatSafetyHoc } from '@entities/chat/contexts';
import { ChatMessage } from '@entities/chat/ui/ChatMessage';
import { ChatMessageInput } from '@entities/chat/ui/ChatMessageInput';
import { ChatTopLine } from '@entities/chat/ui/ChatTopLine';

import { analytics } from '@services/AnalyticsService';
import {
  useGetMessages,
  usePostMessage,
  usePutReadMessages,
} from '@services/hooks/MatchesService';
import { useGetUserById } from '@services/hooks/UsersService';
import { type SSEHandler, useSseEngineContext } from '@services/SSE';
import { refreshMatchesStore } from '@services/StoreRefreshService';

type ChatProps = {
  matchId: string;
};

export const Chat: React.FC<ChatProps> = ({ matchId }) => {
  const emit = useEventEmit();
  const navigate = useNavigate();
  const auth = useAuthStore();

  // Refs for scroll management
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // State
  const [hasOptimisticallyMarkedAsRead, setHasOptimisticallyMarkedAsRead] =
    useState(false);
  const [lastMessageTimeStamp, setLastMessageTimeStamp] = useState<string>();
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);
  const [hasLoadedInitialMessages, setHasLoadedInitialMessages] =
    useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // Get data from unified store
  const {
    matches,
    matchesMeta,
    messages,
    setMessagesForMatch,
    addMessage,
    optimisticallyUpdateMatchOnSend,
  } = useAppStore();
  // The server returns `match_id` as a JS number (matches.id is int4 in
  // Postgres). The URL param from Tanstack Router is always a string. A
  // strict `===` comparison therefore fails for *every* chat — masked
  // forever by an unconditional <Loader/> early-return.
  // Coerce both sides to string so the lookup works regardless of how the
  // driver materialises the id (number / string / BigInt).
  const profile = Array.from(matches.values()).find(
    (candidate) => String(candidate.match_id) === String(matchId),
  );

  // Fallback fetch when the user lands on /chats/:matchId directly (deep
  // link, push notification, or browser refresh) and the requested match is
  // older than the initial paginated /matches window (`initData` only loads
  // `limit=10`). One-shot deep refresh at the server's max+cached size (500)
  // covers ~98% of users (max observed match count: 2487, p100 well under
  // 500 for the affected population). Tracked so it doesn't ping-pong.
  const [deepRefreshAttempted, setDeepRefreshAttempted] = useState(false);
  useEffect(() => {
    if (profile || deepRefreshAttempted) return;
    if (matchesMeta?.state !== 'success') return;
    setDeepRefreshAttempted(true);
    void refreshMatchesStore(useAppStore.getState(), { limit: 500 });
  }, [profile, matchesMeta?.state, deepRefreshAttempted]);
  const currentMatchId = profile?.match_id;
  const currentMessages = currentMatchId
    ? messages.get(currentMatchId) || []
    : [];

  const { trigger: triggerPostMessage } = usePostMessage();
  const { trigger: triggerMessages } = useGetMessages({ matchId });
  const { trigger: triggerPutReadMessages } = usePutReadMessages({ matchId });
  const { data: dataUserById, isLoading: isLoadingUserById } = useGetUserById({
    queryParams: {
      userId: profile?.id ?? null,
    },
  });

  // Optimistically mark chat as read when entering
  useEffect(() => {
    const isUnread = profile && (profile.is_unread || profile.unread_count > 0);

    if (isUnread && !hasOptimisticallyMarkedAsRead && currentMatchId) {
      const { optimisticallyDecrementUnreadChats, setMatchAsRead } =
        useAppStore.getState();
      optimisticallyDecrementUnreadChats();
      setMatchAsRead(currentMatchId);
      triggerPutReadMessages();
      setHasOptimisticallyMarkedAsRead(true);
    }
  }, [
    profile,
    currentMatchId,
    hasOptimisticallyMarkedAsRead,
    triggerPutReadMessages,
  ]);

  // Handle viewport changes (keyboard open/close)
  useEffect(() => {
    const handleViewportChange = () => {
      const newHeight = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(newHeight);
    };

    // Listen to both visual viewport and window resize
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    }
    window.addEventListener('resize', handleViewportChange);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          handleViewportChange,
        );
      }
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [viewportHeight]);

  // Scroll to bottom function with keyboard awareness
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const container = scrollContainerRef.current;
      if (container) {
        // Add small delay for keyboard animations
        const delay = behavior === 'smooth' ? 100 : 0;

        setTimeout(() => {
          if (container) {
            // Use direct scroll positioning for precise bottom alignment
            const targetScrollTop =
              container.scrollHeight - container.clientHeight;

            if (behavior === 'smooth') {
              container.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth',
              });
            } else {
              container.scrollTop = targetScrollTop;
            }
          }
        }, delay);
      }
    },
    [viewportHeight],
  );

  // Auto-scroll when keyboard opens/closes (but not when loading older messages)
  useEffect(() => {
    if (currentMessages.length > 0 && !isLoadingMore) {
      // Only scroll to bottom when keyboard state changes, not when loading messages
      scrollToBottom('smooth');
    }
  }, [viewportHeight, scrollToBottom]); // Removed currentMessages.length to prevent scroll on message load

  // A failed page of older messages is not requested again until its
  // back-off ends.
  const { isBackingOff, canLoadMore, onLoadFailure, onLoadSuccess } =
    useLoadMoreBackoff();

  // Load older messages
  const loadOlderMessages = useCallback(async () => {
    if (isLoadingMore || !hasMore || !currentMatchId) return;

    setIsLoadingMore(true);

    try {
      const response = await triggerMessages({
        before: lastMessageTimeStamp,
        limit: 20,
      });

      if (response.messages.length > 0) {
        const newTimestamp = response.messages[0].created_at;
        setLastMessageTimeStamp(newTimestamp);

        // Preserve scroll position before DOM changes
        const container = scrollContainerRef.current;
        const oldScrollHeight = container?.scrollHeight || 0;
        const oldScrollTop = container?.scrollTop || 0;

        // Prepend new messages
        const existingMessages = currentMessages;
        const newMessages = response.messages.filter(
          (newMsg) => !existingMessages.some((msg) => msg.id === newMsg.id),
        );
        setMessagesForMatch(currentMatchId, [
          ...newMessages,
          ...existingMessages,
        ]);

        // Restore scroll position after React has updated the DOM
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - oldScrollHeight;
            container.scrollTop = oldScrollTop + scrollDiff;
          }
        });
      }

      if (!response.pagination.hasMore) {
        setHasMore(false);
      }

      onLoadSuccess();
    } catch (error) {
      console.error('[CHAT] Error loading older messages:', error);
      onLoadFailure(error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMore,
    lastMessageTimeStamp,
    triggerMessages,
    currentMessages,
    setMessagesForMatch,
    currentMatchId,
    onLoadFailure,
    onLoadSuccess,
  ]);

  // Initial message loading - only when entering chat
  useEffect(() => {
    if (
      currentMatchId &&
      currentMessages.length === 0 &&
      hasMore &&
      !hasLoadedInitialMessages
    ) {
      setHasLoadedInitialMessages(true);
      loadOlderMessages();
    }
  }, [
    currentMatchId,
    currentMessages.length,
    hasMore,
    hasLoadedInitialMessages,
    loadOlderMessages,
  ]);

  // Setup intersection observer for loading older messages - only after initial load
  const topSentinelRef = useIntersectionObserver({
    root: scrollContainerRef.current,
    rootMargin: '100px',
    threshold: 0.1,
    enabled:
      hasMore &&
      !isLoadingMore &&
      !isBackingOff &&
      hasLoadedInitialMessages &&
      currentMessages.length > 0,
    onIntersect: () => {
      if (canLoadMore()) loadOlderMessages();
    },
  });

  // Auto-scroll to bottom only for truly new messages (not when loading older messages)
  useEffect(() => {
    if (
      currentMessages.length > 0 &&
      !isLoadingMore &&
      hasLoadedInitialMessages
    ) {
      if (!hasInitiallyScrolled) {
        // Initial scroll after first load - immediate
        scrollToBottom('auto');
        setHasInitiallyScrolled(true);
      } else {
        // Only scroll for new messages at the end (not prepended older messages)
        const lastMessage = currentMessages[currentMessages.length - 1];
        const isRecentMessage =
          lastMessage &&
          new Date(lastMessage.created_at).getTime() > Date.now() - 60000; // Within last minute

        if (isRecentMessage) {
          scrollToBottom('smooth');
        }
      }
    }
  }, [
    currentMessages.length,
    hasInitiallyScrolled,
    scrollToBottom,
    isLoadingMore,
    hasLoadedInitialMessages,
  ]);

  // Reset all flags when changing chats
  useEffect(() => {
    setHasInitiallyScrolled(false);
    setHasLoadedInitialMessages(false);
    setHasMore(true);
    setLastMessageTimeStamp(undefined);
    setMessagesForMatch(matchId, []);
  }, [matchId]);

  // Handle input focus for keyboard management
  const handleInputFocus = useCallback(() => {
    // Scroll to bottom when input is focused (keyboard opening)
    setTimeout(() => scrollToBottom('smooth'), 300);
  }, [scrollToBottom]);

  // Send message handler
  const onSend = async (text: string) => {
    if (!currentMatchId) {
      return;
    }

    const optimisticMessage = {
      id: Date.now(),
      match_id: currentMatchId,
      sender_id: auth?.user?.id || '',
      content: text,
      created_at: new Date().toISOString(),
      read_at: null,
      is_read: false,
      sender_name: auth?.user?.username || 'You',
    };

    // Track message send attempt
    analytics.trackMessageSendClicked();

    try {
      // Optimistically update UI
      optimisticallyUpdateMatchOnSend(currentMatchId, optimisticMessage);
      addMessage(currentMatchId, optimisticMessage);

      // Send to server
      await triggerPostMessage({ matchId, content: text });

      // Track successful message send
      analytics.trackMessageSendSuccess();

      // Scroll to bottom after sending with keyboard consideration
      setTimeout(() => scrollToBottom('smooth'), 150);
    } catch (error) {
      // Track message send error
      analytics.trackMessageSendError('network_error');

      emit('error', {
        title: 'Не удалось отправить сообщение',
        description: 'Попробуйте еще раз',
      });
    }
  };

  const {
    addSseHandler,
    removeSseHandler,
    addInitialHandler,
    removeInitialHandler,
  } = useSseEngineContext();

  const getMsgs = useCallback(async () => {
    if (!currentMatchId) {
      return;
    }

    const response = await triggerMessages({
      limit: 20,
    });

    if (response.messages.length > 0) {
      // Preserve scroll position before DOM changes
      const container = scrollContainerRef.current;
      const oldScrollHeight = container?.scrollHeight || 0;
      const oldScrollTop = container?.scrollTop || 0;

      // Prepend new messages
      const existingMessages = currentMessages;
      const newMessages = response.messages.filter(
        (newMsg) => !existingMessages.some((msg) => msg.id === newMsg.id),
      );
      setMessagesForMatch(currentMatchId, [
        ...newMessages,
        ...existingMessages,
      ]);

      // Restore scroll position after React has updated the DOM
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          const scrollDiff = newScrollHeight - oldScrollHeight;
          container.scrollTop = oldScrollTop + scrollDiff;
        }
      });
    }
  }, [currentMessages, currentMatchId]);

  useEffect(() => {
    const handler: SSEHandler = async (msg) => {
      if (
        msg.type === 'new_chat_message' &&
        msg.message?.match_id == currentMatchId
      ) {
        await triggerPutReadMessages();
      }
    };

    addSseHandler('new_chat_message', handler);

    return () => {
      removeSseHandler('new_chat_message', handler);
    };
  }, [currentMatchId, triggerPutReadMessages]);

  useEffect(() => {
    const handler = () => {
      getMsgs();
    };

    addInitialHandler(handler);

    return () => {
      removeInitialHandler(handler);
    };
  }, [getMsgs]);

  if (!profile) {
    // Still trying to find the match: matches haven't finished loading yet,
    // or the deep fallback fetch is in flight. Spinner is correct here.
    if (matchesMeta?.state !== 'success' || !deepRefreshAttempted) {
      return <Loader />;
    }
    // Matches finished loading AND we already tried the deep refresh — the
    // match genuinely isn't accessible (deleted/blocked/unmatched). Show a
    // recoverable state instead of an indefinite spinner so users aren't
    // trapped on the chat URL.
    return (
      <Result
        src="/icons/3d-icons/sad.png"
        title="Чат недоступен"
        description="Возможно, собеседник удалил матч или сообщение"
        button={
          <Button
            testId="chat-not-found-back-button"
            onClick={() => navigate({ to: '/chats' })}
          >
            К списку чатов
          </Button>
        }
      />
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ChatTopLine
        profile={profile}
        onClick={() => {
          if (!isLoadingUserById && !dataUserById?.id) {
            return emit('warning', {
              title: 'Неподтвержденный профиль',
              description:
                'После проверки фото и описания дадим посмотреть этот профиль',
            });
          }

          navigate({ to: `/dating/profile/${profile.id}` });
        }}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          flexShrink: 0,
        }}
      />

      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          // Ensure proper height calculation with keyboard
          height: `${viewportHeight - 120}px`, // Approximate height minus header and input
          maxHeight: `${viewportHeight - 120}px`,
        }}
      >
        {/* Empty state */}
        {currentMessages.length === 0 && !isLoadingMore && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Badge testId="chat-badge-empty" text="Сообщений пока нет" />
          </div>
        )}

        {/* Messages with original InfiniteScroll visual structure */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gridGap: 8,
          }}
        >
          {/* Top sentinel for loading older messages */}
          {hasMore && (
            <div
              ref={topSentinelRef}
              style={{ height: '1px', flexShrink: 0 }}
            />
          )}

          {/* Loader exactly like react-infinite-scroller */}
          {isLoadingMore && <Loader style={{ margin: '0 auto' }} key={0} />}

          {currentMessages
            .sort(
              (a, b) =>
                moment(a.created_at).unix() - moment(b.created_at).unix(),
            )
            .map((message, index, messages) => {
              const showBadge =
                index === 0 ||
                !moment(message.created_at).isSame(
                  messages[index - 1].created_at,
                  'day',
                );

              return (
                <div key={message.id}>
                  {showBadge && (
                    <Badge
                      testId="chat-badge-empty"
                      text={moment(message.created_at).format('D MMM')}
                      style={{ margin: '8px auto' }}
                    />
                  )}
                  <ChatMessage
                    type={message.sender_id === profile.id ? 'friend' : 'my'}
                    status={message.read_at ? 'readed' : 'sended'}
                    date={message.created_at}
                    text={message.content}
                  />
                </div>
              );
            })}
        </div>

        <div style={{ height: '1px', flexShrink: 0 }} />
      </div>

      <ChatSafetyHoc
        profile={profile}
        matchId={matchId}
        scrollToBottom={scrollToBottom}
      >
        <ChatMessageInput
          placeholder="Введите сообщение"
          onSend={onSend}
          onFocus={handleInputFocus}
          style={{
            flexShrink: 0,
            borderTop: '1px solid #eee',
          }}
        />
      </ChatSafetyHoc>
    </div>
  );
};

Chat.displayName = 'Chat';
